import SearchIcon from '@mui/icons-material/Search';
import {
  Checkbox,
  FormControlLabel,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { Control, Controller, FieldPathByValue, FieldValues } from 'react-hook-form';
import { DeviceGroup } from 'utils/Lock/Lock';
import { useSearch } from 'v2/hooks/useSearch/useSearch';

const SEARCH_KEYS = ['name'];

const toggleIdInArray = (arr: string[], id: string) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

interface Item {
  id: string;
  name: string;
  viaDeviceGroups?: DeviceGroup[];
}

interface Props<TFieldValues extends FieldValues> {
  items: Item[];
  name: FieldPathByValue<TFieldValues, string[]>;
  control: Control<TFieldValues>;
  searchPlaceholder: string;
}

const GroupCheckboxList = <TFieldValues extends FieldValues>({
  items,
  name,
  control,
  searchPlaceholder
}: Props<TFieldValues>) => {
  const theme = useTheme();
  const { translation } = useTranslation();
  const [search, setSearch] = React.useState('');
  const filteredItems = useSearch({ data: items, search, keys: SEARCH_KEYS });

  return (
    <Stack sx={{ overflowY: 'auto', height: '100%', pr: 1 }}>
      <Stack
        sx={{
          position: 'sticky',
          top: 0,
          backgroundColor: theme.palette.background.paper,
          zIndex: 1,
          pb: 0.5,
          gap: 1
        }}
      >
        <TextField
          id="search-input"
          size="small"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              )
            }
          }}
          fullWidth
        />
      </Stack>
      <Stack gap={0.5}>
        {filteredItems.length === 0 && search && (
          <Typography variant="body2" color="textSecondary" sx={{ py: 1 }}>
            {translation.no_results}
          </Typography>
        )}
        {filteredItems.map((item) => {
          const isIndirect = (item.viaDeviceGroups?.length ?? 0) > 0;
          const tooltipTitle = isIndirect
            ? translation.assigned_through_device_group.replace(
                '{{name}}',
                (item.viaDeviceGroups ?? []).map((deviceGroup) => deviceGroup.name).join(', ')
              )
            : '';

          return (
            <Tooltip key={item.id} title={tooltipTitle} placement="top">
              <Stack
                direction="row"
                padding="6px 12px"
                sx={{ backgroundColor: theme.custom.background.level2, borderRadius: 1 }}
                height="44px"
                justifyContent="space-between"
                alignItems="center"
                alignSelf="stretch"
                alignContent="center"
              >
                <FormControlLabel
                  label={<Typography>{item.name}</Typography>}
                  sx={{ '& .MuiFormControlLabel-label': { width: '100%' } }}
                  control={
                    <Controller
                      name={name}
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          id={`group-${item.id}-checkbox`}
                          checked={isIndirect || field.value.includes(item.id)}
                          disabled={isIndirect}
                          onChange={() => {
                            field.onChange(toggleIdInArray(field.value, item.id));
                          }}
                        />
                      )}
                    />
                  }
                />
              </Stack>
            </Tooltip>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default GroupCheckboxList;
